import type { JSX, ReactNode } from 'react';
import { Logo } from '../Logo';

/** The frame every signed-out page shares with sign-in. */
export function PublicCard({
  subtitle,
  children,
}: {
  readonly subtitle: string;
  readonly children: ReactNode;
}): JSX.Element {
  return (
    <main className="signin">
      <div className="signin__card">
        <h1 className="signin__brand">
          <Logo />
          The Fatigue App
        </h1>
        <p className="signin__sub">{subtitle}</p>
        {children}
      </div>
    </main>
  );
}

/** Announced, because a screen reader user otherwise gets silence after pressing the button. */
export function FormError({ message }: { readonly message: string | null }): JSX.Element | null {
  return message === null ? null : (
    <p className="invite__error" role="alert">
      {message}
    </p>
  );
}

/**
 * Two password fields and the one rule that matters, checked before sending.
 *
 * Length only, matching the pools' policy: character-class rules push people
 * towards predictable substitutions and away from the thing that helps.
 */
export function NewPassword({
  password,
  confirm,
  onPassword,
  onConfirm,
  minLength,
}: {
  readonly password: string;
  readonly confirm: string;
  readonly onPassword: (value: string) => void;
  readonly onConfirm: (value: string) => void;
  readonly minLength: number;
}): JSX.Element {
  return (
    <>
      <label className="invite__field">
        <span className="invite__label">Choose a password</span>
        <input
          className="invite__input"
          type="password"
          autoComplete="new-password"
          required
          minLength={minLength}
          value={password}
          onChange={(event) => onPassword(event.target.value)}
        />
        <span className="invite__hint">
          At least {minLength} characters.
        </span>
      </label>
      <label className="invite__field">
        <span className="invite__label">Type it again</span>
        <input
          className="invite__input"
          type="password"
          autoComplete="new-password"
          required
          value={confirm}
          onChange={(event) => onConfirm(event.target.value)}
        />
      </label>
    </>
  );
}

export function passwordProblem(password: string, confirm: string, minLength: number): string | null {
  if (password.length < minLength) return `The password needs at least ${minLength} characters.`;
  if (password !== confirm) return 'The two passwords do not match.';
  return null;
}
